import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DataService } from 'src/app/services/data.service';
import { SiteService } from 'src/app/services/site.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { DripProgram, DripProgramDetails, Campaign, EmailType } from 'src/app/models/communication/campaign';
import { forkJoin, Observable } from 'rxjs';
import { ConfirmationService } from 'primeng/api';
import { IPendingChangesComponent } from 'src/app/guards/pending-changes.guard';

interface DripProgramEmail {
	campaignID: string;
	campaignName: string;
	sequence: number;
	interval: number;
	catchup: boolean;
}

interface DripProgramModel {
	startDate: Date | null;
	deliveryTime: Date | null;
	pause: boolean;
	daysBetweenCatchup: number;
}

@Component({
  selector: 'app-dripprogram',
  templateUrl: './dripprogram.component.html',
  styleUrls: ['./dripprogram.component.scss']
})
export class DripprogramComponent implements OnInit, IPendingChangesComponent {

	public dripProgram: DripProgram = new DripProgram();
	public dripProgramID: string = '';
	public defaultDripProgramID: string = '';
	public dripPrograms: DripProgram[] = [];
	public selectedDripProgramID: string = '';
	public emails: DripProgramEmail[] = [];
	public availableCampaigns: Campaign[] = [];
	public loading: boolean = false;
	public deliveryTimeDate: Date | null = null; // Separate property for UI binding
	public showCampaignDialog: boolean = false;
	public filteredAvailableCampaigns: Campaign[] = [];
	public campaignSearchTerm: string = "";
	public isDirty: boolean = false;
	public dirtySwitchDialogVisible: boolean = false;
	private nextProgramID: string | null = null;

	// Deprecated static options replaced by API-provided list

	constructor(private dataService: DataService, private siteService: SiteService, private router: Router, private confirmationService: ConfirmationService) { }

	ngOnInit() {
		this.siteService.pageTitle = 'Drip Programs';
		
		this.updateHeaderButtons();

		this.loadInitial();
	}

	private updateHeaderButtons() {
		this.siteService.headerButtons = [
			{ text: 'Save', class: 'success', callback: () => this.save() }
		];
	}

	loadInitial() {
		this.loading = true;
		// First get default ID and list of programs
		forkJoin({
			config: this.dataService.getDefaultDripProgramConfig(),
			list: this.dataService.getDripPrograms(),
		}).subscribe({
			next: (config) => {
				this.defaultDripProgramID = config.config.defaultDripProgramID;
				this.dripPrograms = config.list;
				// Default selection
				this.selectedDripProgramID = this.defaultDripProgramID;
				this.dripProgramID = this.selectedDripProgramID;					// Update header buttons now that we have default ID
					this.updateHeaderButtons();				// Load current selection
				this.loadProgramAndDetails(this.dripProgramID);
			},
			error: (error) => {
				console.error('Error loading drip program configuration/list:', error);
				this.siteService.setMessage('Error loading drip programs', 'error');
				this.loading = false;
			}
		});
	}

	private loadProgramAndDetails(programID: string) {
		this.loading = true;
		forkJoin({
			dripProgram: this.dataService.getDripProgram(programID),
			dripProgramDetails: this.dataService.getDripProgramDetails(programID),
			campaigns: this.dataService.getCampaigns(undefined, undefined, EmailType.Drip)
		}).subscribe({
			next: (data) => {
				this.dripProgramID = programID;
				this.dripProgram = new DripProgram(data.dripProgram);
				// Ensure dripProgramDesc is populated for UI binding
				if (!this.dripProgram.dripProgramDesc && this.dripProgram.dripProgramName) {
					this.dripProgram.dripProgramDesc = this.dripProgram.dripProgramName;
				}
				this.availableCampaigns = data.campaigns;
				this.mapDetailsToEmails(data.dripProgramDetails);
				this.loading = false;
				this.isDirty = false;
			},
			error: (error) => {
				console.error('Error loading drip program details:', error);
				this.siteService.setMessage('Error loading drip program', 'error');
				this.loading = false;
			}
		});
	}

	onProgramSelectionChange(nextID: string) {
		if (!nextID || nextID === this.dripProgramID) return;
		if (this.isDirty) {
			this.nextProgramID = nextID;
			this.dirtySwitchDialogVisible = true;
			return;
		}
		this.selectedDripProgramID = nextID;
		this.updateHeaderButtons();
		this.loadProgramAndDetails(nextID);
	}

	mapDetailsToEmails(details: DripProgramDetails[]) {
		this.emails = details
			.sort((a, b) => a.sequence - b.sequence)
			.map(detail => {
				const campaign = this.availableCampaigns.find(c => c.campaignID === detail.campaignID);
				return {
					campaignID: detail.campaignID,
					campaignName: campaign ? campaign.campaignName : '*** Campaign no longer configured for Drip ***',
					sequence: detail.sequence,
					interval: detail.interval,
					catchup: detail.catchup
				};
			});
		
		// Convert start date string to Date object for UI binding
		if (this.dripProgram.startDate && typeof this.dripProgram.startDate === 'string') {
			this.dripProgram.startDate = new Date(this.dripProgram.startDate);
		}
		
		// Convert delivery time string to Date for UI binding
		if (this.dripProgram.deliveryTime && typeof this.dripProgram.deliveryTime === 'string') {
			const [hours, minutes] = this.dripProgram.deliveryTime.split(':');
			this.deliveryTimeDate = new Date();
			this.deliveryTimeDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
		}
	}

	save() {
		return new Promise<boolean>((resolve, reject) => {
			if (!this.dripProgram || !this.dripProgramID) {
				this.siteService.setMessage('No drip program to save', 'error');
				reject(false);
				return;
			}

			this.loading = true;

			// Convert time back to string format for API
			if (this.deliveryTimeDate) {
				const hours = this.deliveryTimeDate.getHours().toString().padStart(2, '0');
				const minutes = this.deliveryTimeDate.getMinutes().toString().padStart(2, '0');
				this.dripProgram.deliveryTime = `${hours}:${minutes}:00`;
			}

			// Ensure startDate is a proper Date object for JSON serialization
			if (this.dripProgram.startDate && typeof this.dripProgram.startDate === 'string') {
				this.dripProgram.startDate = new Date(this.dripProgram.startDate);
			}

			// Create details array from emails
			const details: DripProgramDetails[] = this.emails.map((email, index) => new DripProgramDetails({
				dripProgramID: this.dripProgramID,
				campaignID: email.campaignID,
				sequence: index + 1,
				interval: email.interval,
				catchup: email.catchup
			}));

			// Save both drip program and details
			forkJoin({
				program: this.dataService.saveDripProgram(this.dripProgram),
				details: this.dataService.saveDripProgramDetails(this.dripProgramID, details)
			}).subscribe({
				next: () => {
					this.siteService.setMessage('Drip program saved successfully');
					// Refresh the list to update dropdown labels
					this.dataService.getDripPrograms().subscribe(list => {
						this.dripPrograms = list;
					});
					this.loading = false;
					this.isDirty = false;
					resolve(true);
				},
				error: (error) => {
					console.error('Error saving drip program:', error);
					this.siteService.setMessage('Error saving drip program', 'error');
					this.loading = false;
					reject(false);
				}
			});
		});
	}

	addProgram() {
		const now = new Date();
		const newProgramID = crypto.randomUUID();
		const newProgram: DripProgram = new DripProgram({
			dripProgramID: newProgramID,
			dripProgramDesc: 'New Program',
			startDate: now,
			deliveryTime: '09:00:00',
			paused: false,
			catchUpInterval: 0
		});
		
		// Add to local list and switch to it immediately without saving
		this.dripPrograms.push(newProgram);
		this.selectedDripProgramID = newProgramID;
		this.dripProgramID = newProgramID;
		this.dripProgram = newProgram;
		this.emails = [];
		
		// Set up delivery time for UI
		const [hours, minutes] = newProgram.deliveryTime.split(':');
		this.deliveryTimeDate = new Date();
		this.deliveryTimeDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
		
		this.updateHeaderButtons();
		this.markDirty();
	}

	deleteProgram() {
		const currentID = this.selectedDripProgramID || this.dripProgramID;
		if (!currentID) return;
		if (currentID === this.defaultDripProgramID) {
			this.siteService.setMessage('Cannot delete the default drip program', 'warn');
			return;
		}
		this.confirmationService.confirm({
			message: 'Delete this drip program? This cannot be undone.',
			header: 'Delete Drip Program',
			acceptLabel: 'Delete',
			rejectLabel: 'Cancel',
			acceptIcon: 'trash',
			rejectIcon: 'times',
			accept: () => {
				this.loading = true;
				this.dataService.deleteDripProgram(currentID).subscribe({
					next: () => {
						forkJoin({
							list: this.dataService.getDripPrograms(),
							config: this.dataService.getDefaultDripProgramConfig()
						}).subscribe(({ list, config }) => {
							this.dripPrograms = list;
							this.defaultDripProgramID = config.defaultDripProgramID;
							// After delete, revert selection to default or first available
							this.selectedDripProgramID = this.defaultDripProgramID || (list[0]?.dripProgramID ?? '');
							this.updateHeaderButtons();
							if (this.selectedDripProgramID) {
								this.loadProgramAndDetails(this.selectedDripProgramID);
							}
						});
					},
					error: (error) => {
						console.error('Error deleting drip program:', error);
						this.siteService.setMessage('Error deleting drip program', 'error');
						this.loading = false;
					}
				});
			}
		});
	}

	dropEmail(event: CdkDragDrop<DripProgramEmail[]>) {
		moveItemInArray(this.emails, event.previousIndex, event.currentIndex);
		// Update sequence numbers after reordering
		this.emails.forEach((email, index) => {
			email.sequence = index + 1;
		});
		this.markDirty();
	}

	showAddCampaignDialog() {
		// Get campaigns not already in the sequence
		const usedCampaignIDs = this.emails.map(e => e.campaignID);
		this.filteredAvailableCampaigns = this.availableCampaigns.filter(c => !usedCampaignIDs.includes(c.campaignID));
		
		if (this.filteredAvailableCampaigns.length === 0) {
			this.siteService.setMessage('No more campaigns available to add', 'warn');
			return;
		}

		this.campaignSearchTerm = "";
		this.showCampaignDialog = true;
	}

	filterCampaigns() {
		const usedCampaignIDs = this.emails.map(e => e.campaignID);
		this.filteredAvailableCampaigns = this.availableCampaigns
			.filter(c => !usedCampaignIDs.includes(c.campaignID))
			.filter(c => c.campaignName.toLowerCase().includes(this.campaignSearchTerm.toLowerCase()));
	}

	selectCampaignToAdd(campaign: Campaign) {
		this.addCampaign(campaign.campaignID, campaign.campaignName);
		this.closeCampaignDialog();
	}

	closeCampaignDialog() {
		this.showCampaignDialog = false;
		this.campaignSearchTerm = "";
	}

	addCampaign(campaignID: string, campaignName: string) {
		const newEmail: DripProgramEmail = {
			campaignID: campaignID,
			campaignName: campaignName,
			sequence: this.emails.length + 1,
			interval: 7, // Default to 7 days
			catchup: false
		};
		this.emails.push(newEmail);
		this.markDirty();
	}

	removeCampaign(index: number) {
		this.confirmationService.confirm({
			message: 'Are you sure you want to remove this campaign from the sequence?',
			header: 'Remove Campaign?',
			acceptLabel: 'Remove',
			rejectLabel: 'Cancel',
			acceptIcon: 'check',
			rejectIcon: 'times',
			rejectButtonStyleClass: 'danger',
			accept: () => {
				this.emails.splice(index, 1);
				// Update sequence numbers after removal
				this.emails.forEach((email, idx) => {
					email.sequence = idx + 1;
				});
				this.markDirty();
			}
		});
	}

	markDirty() {
		this.isDirty = true;
	}

	canDeactivate(): boolean | Observable<boolean> {
		return !this.isDirty;
	}

	// Dirty switch dialog handlers
	onDirtySave() {
		if (!this.nextProgramID) { this.dirtySwitchDialogVisible = false; return; }
		this.save().then(() => {
			const target = this.nextProgramID as string;
			this.dirtySwitchDialogVisible = false;
			this.nextProgramID = null;
			this.selectedDripProgramID = target;
			this.updateHeaderButtons();
			this.loadProgramAndDetails(target);
		});
	}

	onDirtyDiscard() {
		if (!this.nextProgramID) { this.dirtySwitchDialogVisible = false; return; }
		const target = this.nextProgramID as string;
		this.dirtySwitchDialogVisible = false;
		this.nextProgramID = null;
		this.isDirty = false;
		this.selectedDripProgramID = target;
		this.updateHeaderButtons();
		this.loadProgramAndDetails(target);
	}

	onDirtyCancel() {
		this.dirtySwitchDialogVisible = false;
		this.nextProgramID = null;
	}
}
